import { useEffect, useState } from "react";

import { UseFormReturn } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import Form from "../../components/base/Form/Form";
import { FormSelect } from "../../components/base/Form/FormSelect";
import { FormTextField } from "../../components/base/Form/FormTextField";
import { FormToggle } from "../../components/base/Form/FormToggle";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useApplication from "../../data/Application/useApplication";
import useApplicationMutations from "../../data/Application/useApplicationMutations";
import { CreateApplicationFormFields } from "../../models/Application/CreateApplication";
import {
  ApplicationStatus,
  ApplicationVisability,
} from "../../models/Application/Application";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";
import { errorResolver } from "../../utils/errorResolver";

export function EditApplication() {
  const { applicationId } = useParams();
  const { application } = useApplication(applicationId!);
  const { update } = useApplicationMutations();
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const updateApplication = (data: CreateApplicationFormFields) => {
    if (!applicationId) {
      return;
    }

    setSubmitting(true);
    update.mutate(
      {
        id: applicationId,
        ...data,
        status: parseInt(data.status as unknown as string),
        visability: parseInt(data.visability as unknown as string),
      },
      {
        onSuccess: () => {
          navigate(`/application/${applicationId}`);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  useEffect(() => {
    if (!application.isLoading && application.data) {
      setInfo({
        name: application.data.name,
        desc: `Edit ${application.data.name} application`,
        pageTitle: `Edit Application | ${application.data.name}`,
      });
    } else {
      setInfo({
        name: "Edit Application",
        desc: "Edit application details",
        pageTitle: "Edit Application",
      });
    }
  }, [application.isLoading, application.data, setInfo]);

  if (!applicationId) {
    return <Navigate to="/applications" />;
  } else if (application.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (application.isError || !application.data) {
    return (
      <Alert
        severity="error"
        title="Error getting application"
        message={errorResolver(application.error)}
      />
    );
  } else if (submitting) {
    return (
      <div className="py-8">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <div className="bg-white rounded shadow p-4">
        <Form<CreateApplicationFormFields>
          onSubmit={updateApplication}
          defaultValues={{
            name: application.data.name,
            status: application.data.status,
            visability: application.data.visability,
            system: application.data.system,
            allowSignup: application.data.allowSignup,
            systemApplicationSettingsConnectionString:
              application.data.systemApplicationSettingsConnectionString,
          }}
          className="grid grid-cols-1 gap-y-4 md:w-3/4 md:grid-cols-2 md:gap-4"
        >
          {({
            register,
            control,
          }: UseFormReturn<CreateApplicationFormFields>) => (
            <>
              <div className="col-span-2 grid grid-cols-1 gap-y-4 md:gap-4 md:px-4 md:grid-cols-2">
                <div className="md:col-span-2 border-b">
                  <p className="text-base font-medium text-gray-600 font-stick pb-2">
                    Basic Information
                  </p>
                </div>
                <FormTextField
                  {...register("name", { required: true })}
                  placeholder="Hubspot"
                  label="Application Name"
                />
                <FormSelect {...register("status")} label="Status">
                  {enumToSelectOptions(ApplicationStatus).map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </FormSelect>
                <FormSelect {...register("visability")} label="Visability">
                  {enumToSelectOptions(ApplicationVisability).map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </FormSelect>
                <div />
                <div className="mt-2">
                  <FormToggle<CreateApplicationFormFields>
                    control={control}
                    name="system"
                    toggleProps={{ label: "System Application" }}
                  />
                </div>
                <div className="mt-2">
                  <FormToggle<CreateApplicationFormFields>
                    control={control}
                    name="allowSignup"
                    toggleProps={{ label: "Allow Sign Up" }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-y-4 md:col-span-2 md:gap-4 md:px-4 md:pb-4 md:grid-cols-2">
                <div className="col-span-2 mt-4 pt-4 grid grid-cols-4 gap-4 border-t">
                  <Button type="submit">Save Application</Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => navigate(`/application/${applicationId}`)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}
        </Form>
      </div>
    );
  }
}
